import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { quizSets } from '../quizSets';
import './Ranking.css';

const Ranking = ({ onBack, initialSetId }) => {
  const [selectedSetId, setSelectedSetId] = useState(initialSetId || 'all');
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchRankings = async () => {
    setLoading(true);
    setError('');

    try {
      let q;

      if (selectedSetId === 'all') {
        // 総合ランキング
        q = query(
          collection(db, 'scores'),
          orderBy('percentage', 'desc'),
          orderBy('timestamp', 'asc'),
          limit(10)
        );
      } else {
        // 各セット別ランキング
        q = query(
          collection(db, 'scores'),
          where('setId', '==', selectedSetId),
          orderBy('percentage', 'desc'),
          orderBy('timestamp', 'asc'),
          limit(10)
        );
      }

      const querySnapshot = await getDocs(q);
      const rankingData = [];

      querySnapshot.forEach((doc) => {
        rankingData.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setRankings(rankingData);
      setLoading(false);
      setError(''); // 成功したらエラーをクリア
    } catch (error) {
      console.error('Error fetching rankings:', error);

      // Firestoreのインデックスエラーかどうかをチェック
      if (error.code === 'failed-precondition' || error.message.includes('index')) {
        setError('Firestoreのインデックスを作成中です。しばらくお待ちください。');
      } else {
        setError('ランキングの取得に失敗しました。もう一度お試しください。');
      }

      setRankings([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRankings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSetId]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMedalEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `${rank}位`;
  };

  const getSelectedSetTitle = () => {
    if (selectedSetId === 'all') return '総合ランキング';
    const set = quizSets.find(s => s.id === selectedSetId);
    return set ? set.title : 'ランキング';
  };

  return (
    <div className="ranking-container">
      <h1 className="ranking-title">{getSelectedSetTitle()}</h1>
      <p className="ranking-subtitle">TOP 10</p>

      {/* セット選択タブ */}
      <div className="set-selector-tabs">
        <button
          className={`tab-button ${selectedSetId === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedSetId('all')}
        >
          総合
        </button>
        {quizSets.map((set) => (
          <button
            key={set.id}
            className={`tab-button ${selectedSetId === set.id ? 'active' : ''}`}
            onClick={() => setSelectedSetId(set.id)}
          >
            セット{set.id}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading">読み込み中...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : rankings.length === 0 ? (
        <div className="no-data">まだランキングがありません</div>
      ) : (
        <div className="ranking-list">
          {rankings.map((entry, index) => (
            <div key={entry.id} className={`ranking-item rank-${index + 1}`}>
              <div className="rank-badge">{getMedalEmoji(index + 1)}</div>
              <div className="player-info">
                <div className="player-name">{entry.name}</div>
                <div className="player-meta">
                  {entry.setTitle && selectedSetId === 'all' && (
                    <span className="set-label">{entry.setTitle}</span>
                  )}
                  <span className="player-date">{formatDate(entry.timestamp)}</span>
                </div>
              </div>
              <div className="player-score">
                <div className="score-percentage">{entry.percentage}%</div>
                <div className="score-detail">
                  {entry.score} / {entry.totalQuestions}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <button className="back-button" onClick={onBack}>
        タイトルに戻る
      </button>
    </div>
  );
};

export default Ranking;
